<template>
  <div class="china-map">
    <div class="radar-bg">
      <div class="radar-sweep"></div>
      <div class="radar-ring ring-1"></div>
      <div class="radar-ring ring-2"></div>
      <div class="radar-ring ring-3"></div>
    </div>
    <div id="chinaMapChart" class="chart-container" :style="chartTransform"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import '@/echarts/china.js'
import { cityOrderCount, provinceOrderCount } from '@/api/agriculture-dashboard'
import to from '@/utils/await-to'

let myChart

const PROVINCE_COORDS = {
  湖北省: [112.59, 30.93],
  北京市: [116.2, 40.2],
  天津市: [117.2, 39.1],
  上海市: [121.4, 31.3],
  重庆市: [106.54, 29.59],
  河北省: [114.48, 38.03],
  山西省: [112.55, 37.87],
  辽宁省: [123.43, 41.8],
  吉林省: [125.35, 43.88],
  黑龙江省: [126.63, 45.75],
  江苏省: [118.78, 32.04],
  浙江省: [120.19, 30.26],
  安徽省: [117.27, 31.86],
  福建省: [119.3, 26.08],
  江西省: [115.89, 28.68],
  山东省: [117.0, 36.65],
  河南省: [113.65, 34.76],
  湖南省: [112.94, 28.23],
  广东省: [113.27, 23.13],
  海南省: [110.35, 20.02],
  四川省: [104.06, 30.67],
  贵州省: [106.71, 26.57],
  云南省: [102.73, 25.04],
  陕西省: [108.95, 34.27],
  甘肃省: [103.83, 36.06],
  青海省: [101.78, 36.62],
  台湾省: [121.5, 25.05],
  内蒙古自治区: [111.65, 40.82],
  广西壮族自治区: [108.33, 22.84],
  西藏自治区: [91.11, 29.97],
  宁夏回族自治区: [106.27, 38.47],
  新疆维吾尔自治区: [87.68, 43.77],
  香港特别行政区: [114.17, 22.28],
  澳门特别行政区: [113.55, 22.2],
}

const CITY_COORDS = {
  武汉市: [114.31, 30.52],
  黄石市: [115.04, 30.2],
  十堰市: [110.79, 32.65],
  宜昌市: [111.29, 30.69],
  襄阳市: [112.14, 32.01],
  鄂州市: [114.89, 30.39],
  荆门市: [112.2, 31.04],
  孝感市: [113.92, 30.92],
  荆州市: [112.24, 30.33],
  黄冈市: [114.87, 30.45],
  咸宁市: [114.32, 29.84],
  随州市: [113.38, 31.69],
  恩施市: [109.49, 30.27],
  北京市: [116.46, 39.92],
  上海市: [121.48, 31.22],
  广州市: [113.27, 23.13],
  深圳市: [114.07, 22.62],
  成都市: [104.06, 30.67],
  西安市: [108.95, 34.27],
  南京市: [118.78, 32.04],
  杭州市: [120.19, 30.26],
  重庆市: [106.54, 29.59],
  长沙市: [112.94, 28.23],
  郑州市: [113.65, 34.76],
  合肥市: [117.27, 31.86],
  济南市: [117.0, 36.65],
  福州市: [119.3, 26.08],
  昆明市: [102.73, 25.04],
  贵阳市: [106.71, 26.57],
  南昌市: [115.89, 28.68],
  太原市: [112.55, 37.87],
  沈阳市: [123.43, 41.8],
  长春市: [125.35, 43.88],
  哈尔滨市: [126.63, 45.75],
  石家庄市: [114.48, 38.03],
  兰州市: [103.83, 36.06],
  南宁市: [108.33, 22.84],
  海口市: [110.35, 20.02],
  呼和浩特市: [111.65, 40.82],
  拉萨市: [91.11, 29.97],
  银川市: [106.27, 38.47],
  乌鲁木齐市: [87.68, 43.77],
  西宁市: [101.78, 36.62],
  天津市: [117.2, 39.1],
  苏州市: [120.62, 31.3],
  无锡市: [120.3, 31.57],
  宁波市: [121.55, 29.87],
  温州市: [120.7, 28.0],
  东莞市: [113.75, 23.04],
  佛山市: [113.12, 23.02],
  珠海市: [113.58, 22.27],
  厦门市: [118.1, 24.46],
  青岛市: [120.38, 36.07],
  大连市: [121.62, 38.91],
  烟台市: [121.45, 37.46],
  常州市: [119.97, 31.78],
  南通市: [120.86, 32.01],
  扬州市: [119.42, 32.39],
  徐州市: [117.19, 34.26],
  绍兴市: [120.58, 30.0],
  嘉兴市: [120.76, 30.77],
  金华市: [119.65, 29.08],
}

export default {
  name: 'ChinaMap',
  data() {
    return {
      mousePercentX: 0.5,
      mousePercentY: 0.5,
      flyLineData: [],
      provinceScatterData: [],
      scatterData: [],
      pollTimer: null,
    }
  },
  computed: {
    chartTransform() {
      const rotateY = (this.mousePercentX - 0.5) * 8
      const rotateX = 8 - (this.mousePercentY - 0.5) * 6
      return {
        transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
      }
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
  },
  mounted() {
    this.initChart()
    this.fetchMapData()
    this.startPolling()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.stopPolling()
    window.removeEventListener('resize', this.handleResize)
    if (myChart) {
      myChart.dispose()
      myChart = null
    }
  },
  methods: {
    initChart() {
      const chartDom = document.getElementById('chinaMapChart')
      if (!chartDom) return
      myChart = echarts.init(chartDom)
      this.setOption()
      myChart.getZr().on('mousemove', params => {
        const el = this.$el
        if (!el) return
        const rect = el.getBoundingClientRect()
        this.mousePercentX = (params.offsetX || 0) / rect.width
        this.mousePercentY = (params.offsetY || 0) / rect.height
      })
      myChart.getZr().on('mouseout', () => {
        this.mousePercentX = 0.5
        this.mousePercentY = 0.5
      })
    },
    async fetchMapData() {
      const [cityRes] = await to(cityOrderCount({}))
      const [provRes] = await to(provinceOrderCount({}))

      if (cityRes && cityRes.data) {
        const cities = cityRes.data
          .filter(c => CITY_COORDS[c.city])
          .map(c => ({
            name: c.city,
            orderCount: c.orderCount,
            borderCount: c.borderCount,
            corderCount: c.corderCount,
            thirdOrderCount: c.thirdOrderCount,
            orderAmount: c.orderAmount,
            borderAmount: c.borderAmount,
            corderAmount: c.corderAmount,
            thirdOrderAmount: c.thirdOrderAmount,
          }))
        const counts = cities.map(c => c.orderCount)
        const minC = Math.min(...counts)
        const maxC = Math.max(...counts)
        this.scatterData = cities
          .map(c => {
            const s = maxC === minC ? 30 : 20 + (40 * (c.orderCount - minC)) / (maxC - minC)
            return {
              name: c.name,
              value: [
                ...CITY_COORDS[c.name],
                s,
                c.orderCount,
                c.borderCount,
                c.corderCount,
                c.thirdOrderCount,
                c.orderAmount,
                c.borderAmount,
                c.corderAmount,
                c.thirdOrderAmount,
              ],
            }
          })
          .sort((a, b) => a.value[2] - b.value[2])
      }

      if (provRes && provRes.data) {
        const topProvinces = provRes.data
          .filter(p => p.borderCount > 0)
          .sort((a, b) => b.borderCount - a.borderCount)
          .slice(0, 10)

        this.flyLineData = topProvinces
          .filter(p => PROVINCE_COORDS[p.province])
          .map(p => ({
            name: p.province,
            borderCount: p.borderCount,
            coords: [PROVINCE_COORDS['湖北省'], PROVINCE_COORDS[p.province]],
          }))

        const scatterSet = new Set(topProvinces.map(p => p.province))
        scatterSet.add('湖北省')

        const scatterNames = [...scatterSet].filter(name => PROVINCE_COORDS[name])
        const scatterValues = scatterNames.map(name => {
          const provData = topProvinces.find(p => p.province === name)
          return name === '湖北省' ? 150 : provData ? provData.borderCount : 0
        })
        const minP = Math.min(...scatterValues)
        const maxP = Math.max(...scatterValues)

        this.provinceScatterData = scatterNames.map((name, idx) => {
          const val = scatterValues[idx]
          const s = maxP === minP ? 18 : 12 + (24 * (val - minP)) / (maxP - minP)
          return {
            name,
            value: [...PROVINCE_COORDS[name], s, val],
          }
        })
      }

      this.setOption()
    },
    startPolling() {
      this.fetchMapData()
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        this.fetchMapData()
      }, 30000)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    setOption() {
      const option = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(5, 13, 76, 0.9)',
          borderColor: '#00B4D8',
          borderWidth: 1,
          textStyle: {
            color: '#E2EFFF',
            fontSize: 18,
          },
          formatter: function(params) {
            if (params.seriesName === 'provinceScatter') {
              return `<div style="font-weight:bold;color:#FFD700">${
                params.name
              }</div><div>B端销售单量: ${Math.round(params.value[3] || 0)}</div>`
            }
            if (params.seriesName === 'cityScatter') {
              return `<div style="font-weight:bold;color:#00E5FF">${params.name}</div>
                <div>订单总量: ${Math.round(params.value[3] || 0)} | 金额: ${(
                params.value[7] || 0
              ).toFixed(2)}</div>
                <div>B端销售单量: ${Math.round(params.value[4] || 0)} | 金额: ${(
                params.value[8] || 0
              ).toFixed(2)}</div>
                <div>C端销售单量: ${Math.round(params.value[5] || 0)} | 金额: ${(
                params.value[9] || 0
              ).toFixed(2)}</div>
                <div>第三方订单: ${Math.round(params.value[6] || 0)} | 金额: ${(
                params.value[10] || 0
              ).toFixed(2)}</div>`
            }
            if (params.seriesType === 'lines') {
              return `<div style="font-weight:bold;color:#FFD700">飞线</div><div>目的地: ${params.name}</div><div>B端单量: ${params.data.borderCount}</div>`
            }
            return params.name
          },
        },
        geo: [
          {
            map: 'china',
            roam: false,
            zoom: 1.2,
            center: [105, 36.5],
            zlevel: 0,
            silent: true,
            label: { show: false },
            itemStyle: {
              areaColor: '#0088aa',
              borderColor: 'transparent',
              borderWidth: 0,
            },
          },
          {
            map: 'china',
            roam: false,
            zoom: 1.2,
            center: [105, 36],
            zlevel: 1,
            label: { show: false },
            itemStyle: {
              areaColor: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(10, 55, 95, 0.95)' },
                  { offset: 1, color: 'rgba(6, 30, 60, 0.95)' },
                ],
              },
              borderColor: '#00B4D8',
              borderWidth: 1,
              shadowColor: 'rgba(0, 180, 216, 0.3)',
              shadowBlur: 12,
            },
            emphasis: {
              itemStyle: {
                areaColor: '#0088cc',
                borderColor: '#00B4D8',
                borderWidth: 2,
              },
              label: {
                show: true,
                color: '#fff',
                fontSize: 14,
              },
            },
            regions: [
              {
                name: '湖北',
                itemStyle: {
                  areaColor: {
                    type: 'linear',
                    x: 0,
                    y: 0,
                    x2: 0,
                    y2: 1,
                    colorStops: [
                      { offset: 0, color: '#0088cc' },
                      { offset: 1, color: '#0057aa' },
                    ],
                  },
                  borderColor: '#00B4D8',
                  borderWidth: 1.5,
                },
              },
            ],
          },
        ],
        series: [
          {
            type: 'lines',
            coordinateSystem: 'geo',
            geoIndex: 1,
            zlevel: 5,
            effect: {
              show: true,
              period: 4,
              trailLength: 0.4,
              symbol: 'arrow',
              symbolSize: 6,
              color: '#FFD700',
            },
            lineStyle: {
              opacity: 0.6,
              curveness: 0.3,
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 1,
                y2: 0,
                colorStops: [
                  { offset: 0, color: 'rgba(0, 180, 216, 0.1)' },
                  { offset: 1, color: 'rgba(255, 215, 0, 0.8)' },
                ],
              },
            },
            data: this.flyLineData.map((item, idx, arr) => {
              const minW = 1,
                maxW = 6
              const counts = arr.map(a => a.borderCount)
              const minC = Math.min(...counts)
              const maxC = Math.max(...counts)
              const w =
                maxC === minC
                  ? 3
                  : minW + ((maxW - minW) * (item.borderCount - minC)) / (maxC - minC)
              return {
                coords: item.coords,
                name: item.name,
                borderCount: item.borderCount,
                value: item.borderCount,
                lineStyle: {
                  width: w,
                },
              }
            }),
          },
          {
            type: 'effectScatter',
            coordinateSystem: 'geo',
            geoIndex: 1,
            name: 'provinceScatter',
            zlevel: 4,
            rippleEffect: {
              brushType: 'stroke',
              scale: 3,
              period: 4,
            },
            symbol: 'circle',
            symbolSize: function(val) {
              return val[2]
            },
            itemStyle: {
              color: {
                type: 'radial',
                x: 0.5,
                y: 0.5,
                r: 0.5,
                colorStops: [
                  { offset: 0, color: 'rgba(255, 235, 132, 1)' },
                  { offset: 0.6, color: 'rgba(255, 193, 7, 0.9)' },
                  { offset: 1, color: 'rgba(255, 152, 0, 0.5)' },
                ],
              },
              shadowColor: 'rgba(255, 215, 0, 0.3)',
              shadowBlur: 5,
            },
            label: {
              show: true,
              position: 'right',
              formatter: '{b}',
              color: 'rgba(255, 235, 132, 0.95)',
              fontSize: 18,
              fontWeight: 'bold',
              textShadowColor: 'rgba(255, 193, 7, 0.8)',
              textShadowBlur: 6,
            },
            data: this.provinceScatterData,
          },
          {
            type: 'effectScatter',
            coordinateSystem: 'geo',
            geoIndex: 1,
            name: 'cityScatter',
            zlevel: 3,
            rippleEffect: {
              brushType: 'stroke',
              scale: 2.5,
              period: 3,
            },
            symbol: 'circle',
            symbolSize: function(val) {
              return val[2]
            },
            itemStyle: {
              color: {
                type: 'radial',
                x: 0.5,
                y: 0.5,
                r: 0.5,
                colorStops: [
                  { offset: 0, color: 'rgba(0, 229, 255, 1)' },
                  { offset: 0.6, color: 'rgba(68, 139, 255, 0.9)' },
                  { offset: 1, color: 'rgba(0, 87, 255, 0.4)' },
                ],
              },
              shadowColor: 'rgba(0, 229, 255, 0.3)',
              shadowBlur: 5,
            },
            label: {
              show: true,
              position: 'right',
              formatter: '{b}',
              color: 'rgba(226, 239, 255, 0.9)',
              fontSize: 15,
              fontWeight: 'bold',
              textShadowColor: 'rgba(0, 180, 216, 0.6)',
              textShadowBlur: 4,
            },
            data: this.scatterData,
          },
        ],
      }
      myChart.setOption(option, true)
    },
    handleResize() {
      if (myChart) {
        myChart.resize()
      }
    },
    handleMouseMove(e) {
      const el = this.$el
      if (!el) return
      const rect = el.getBoundingClientRect()
      this.mousePercentX = (e.clientX - rect.left) / rect.width
      this.mousePercentY = (e.clientY - rect.top) / rect.height
    },
    handleMouseLeave() {
      this.mousePercentX = 0.5
      this.mousePercentY = 0.5
    },
  },
}
</script>

<style lang="scss" scoped>
.china-map {
  width: 100%;
  height: 100%;
  perspective: 1600px;
  perspective-origin: 50% 50%;
  position: relative;

  .radar-bg {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1500px;
    height: 1500px;
    margin-left: -750px;
    margin-top: -750px;
    pointer-events: none;

    .radar-sweep {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: conic-gradient(
        from 0deg,
        rgba(0, 180, 216, 0.08) 0deg,
        rgba(0, 180, 216, 0.02) 30deg,
        transparent 60deg,
        transparent 360deg
      );
      animation: radarSweep 6s linear infinite;
    }

    .radar-ring {
      position: absolute;
      top: 50%;
      left: 50%;
      border-radius: 50%;
      border: 1px solid rgba(0, 180, 216, 0.15);
      transform: translate(-50%, -50%);
    }

    .ring-1 {
      width: 550px;
      height: 550px;
    }

    .ring-2 {
      width: 950px;
      height: 950px;
    }

    .ring-3 {
      width: 1350px;
      height: 1350px;
    }
  }

  .chart-container {
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 10;
    transform: rotateX(8deg) rotateY(0deg);
    transform-style: preserve-3d;
    filter: drop-shadow(0 20px 60px rgba(0, 180, 216, 0.15));
    transition: transform 0.3s ease-out;
  }

  @keyframes radarSweep {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
}
</style>
